import { Component, HostListener, OnInit, OnDestroy, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AuthService } from '@auth0/auth0-angular';
import { MediatorService } from './shared/services/mediator.service';
import { OpenSearchComponentDropdown } from './shared/models/mediator-messages.model';
import { Idle, DEFAULT_INTERRUPTSOURCES } from '@ng-idle/core';
import { MessageService } from 'primeng/api';
import { Router } from '@angular/router';
import { filter, switchMap, take, Subject, takeUntil, Subscription } from 'rxjs';
import { AppInitializerService } from './core/services/app-initializer.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {

  public environment: string;
  public idleState = signal<string>('');
  public timedOut = signal<boolean>(false);
  public isLoading = toSignal(this.auth.isLoading$);

  private _destroy$ = new Subject<void>();
  private _idleSubscriptions: Subscription[] = [];
  private _defaultIdleSeconds: number;

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (event.code === 'F3' || (event.ctrlKey && event.code === 'KeyF')) {
      this._mediator.publish(new OpenSearchComponentDropdown());
      event.preventDefault();
    }
  }

  constructor(
    public auth: AuthService,
    private _idle: Idle,
    private _messageService: MessageService,
    private _mediator: MediatorService,
    private _router: Router,
    private _appInitializer: AppInitializerService
  ) {
    const config = this._appInitializer.initialize();
    this.environment = config.environment;
    this._defaultIdleSeconds = config.defaultIdleSeconds;

    this.initializeIdleTimer();
    this.subscribeToAuthUser();
  }

  ngOnInit(): void {
    this.auth.isLoading$.pipe(
      filter(loading => !loading),
      switchMap(() => this.auth.isAuthenticated$),
      take(1),
      takeUntil(this._destroy$)
    ).subscribe(isAuthenticated => {
      if (!isAuthenticated) {
        console.warn('[AppComponent] User not authenticated. Redirecting to login...');
        this._router.navigate(['/login']);
      }
    });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
    this._idleSubscriptions.forEach(sub => sub.unsubscribe());
  }

  handleIdleEnd() {
    this._messageService.clear("idle");
    this.resetIdleWatch();
  }

  handleTimeout() {
    this.idleState.set('Timed out');
    this.timedOut.set(true);
    this.auth.logout({ logoutParams: { returnTo: window.location.origin, federated: true } });
  }

  handleIdleStart() {
    this.idleState.set('Idle session detected.');
    this._messageService.add({
      key: "idle",
      life: 1000,
      sticky: true,
      severity: 'warn',
      summary: 'Idle Session'
    });
  }

  handleTimeoutWarning(countdown: number) {
    this.idleState.set(`Session will time out in ${countdown} seconds.`);
  }

  onIdleWarningClose() {
    this.resetIdleWatch();
  }

  resetIdleWatch() {
    this.idleState.set('');
    this.timedOut.set(false);
    this._idle.watch();
  }

  private subscribeToAuthUser(): void {
    const authUserSub = this.auth.user$?.subscribe((user) => {
      if (user) {
        this._idle.watch();
        this.timedOut.set(false);
      } else {
        this._idle.stop();
      }
    });

    if (authUserSub) {
      this._idleSubscriptions.push(authUserSub);
    }
  }

  private initializeIdleTimer(): void {
    this._idle.setIdle(this._defaultIdleSeconds);
    this._idle.setTimeout(15); // seconds

    this._idleSubscriptions.push(
      this._idle.onIdleEnd.subscribe(() => this.handleIdleEnd()),
      this._idle.onTimeout.subscribe(() => this.handleTimeout()),
      this._idle.onIdleStart.subscribe(() => this.handleIdleStart()),
      this._idle.onTimeoutWarning.subscribe((countdown) => this.handleTimeoutWarning(countdown))
    );

    this._idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
  }
}
